import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Mail, CheckCircle, Loader2, Info, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [formData, setFormData] = useState({
    is_member: '',
    topic: '',
    company_name: '',
    contact_person: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.is_member || !formData.topic || !formData.company_name || 
        !formData.contact_person || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to send message');
      
      setSubmitted(true);
      setFormData({
        is_member: '',
        topic: '',
        company_name: '',
        contact_person: '',
        email: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="contact-page">
      <SEO 
        title="Contact AfriCycle Zambia | EPR Compliance Support"
        description="Get in touch with AfriCycle Zambia for EPR compliance inquiries, plastic packaging and WEEE recycling questions, or partnership opportunities. Contact Dr. Kachikoti Banda and our team."
        keywords="contact AfriCycle Zambia, EPR compliance support Zambia, plastic recycling inquiry, WEEE compliance contact, environmental services Lusaka"
        canonicalUrl="https://africycle.pro/contact"
      />
      {/* Header */}
      <section className="bg-white py-12 border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#D2E4D6] text-[#056839] mb-4">
            <Mail className="h-7 w-7" />
          </div>
          <h1 className="text-4xl font-bold text-[#056839] mb-4">CONTACT US</h1>
          <p className="text-lg text-[#3F3F3F]">
            Send us your request and our team will respond shortly.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            /* Success Message */
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center animate-fade-in" data-testid="contact-success">
              <div className="w-16 h-16 rounded-full bg-[#D2E4D6] flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-[#056839]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#056839] mb-2">Thank you for your request.</h2>
              <p className="text-[#3F3F3F]">Our team will get back to you shortly.</p>
              <Button 
                onClick={() => setSubmitted(false)}
                className="mt-6 bg-[#056839] text-white hover:bg-[#04522d] rounded-full px-6 py-3"
                data-testid="send-another-btn"
              >
                Send Another Message
              </Button>
            </div>
          ) : (
            <>
              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8" data-testid="contact-form">
                {/* Are you already a member? */}
                <div className="mb-6">
                  <Label className="text-[#3F3F3F] font-medium mb-3 block">
                    Are you already a member? <span className="text-red-500">*</span>
                  </Label>
                  <RadioGroup 
                    value={formData.is_member} 
                    onValueChange={(v) => setFormData({ ...formData, is_member: v })}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="member-yes" />
                      <Label htmlFor="member-yes" className="cursor-pointer">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="member-no" />
                      <Label htmlFor="member-no" className="cursor-pointer">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Topic */}
                <div className="mb-6">
                  <Label className="text-[#3F3F3F] font-medium mb-2 block">
                    Topic <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={formData.topic} 
                    onValueChange={(v) => setFormData({ ...formData, topic: v })}
                  >
                    <SelectTrigger className="h-12" data-testid="topic-select">
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="membership">Membership</SelectItem>
                      <SelectItem value="epr_fees">EPR fees for plastic packaging</SelectItem>
                      <SelectItem value="customized_package">Customized package</SelectItem>
                      <SelectItem value="others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Company Name */}
                <div className="mb-6">
                  <Label className="text-[#3F3F3F] font-medium mb-2 block">
                    Company Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Your company name"
                    className="h-12"
                    data-testid="company-name-input"
                  />
                </div>

                {/* Contact Person */}
                <div className="mb-6">
                  <Label className="text-[#3F3F3F] font-medium mb-2 block">
                    Contact Person <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    placeholder="Your name"
                    className="h-12"
                    data-testid="contact-person-input"
                  />
                </div>

                {/* Email */}
                <div className="mb-6">
                  <Label className="text-[#3F3F3F] font-medium mb-2 block">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@company.com"
                    className="h-12"
                    data-testid="email-input"
                  />
                </div>

                {/* Message */}
                <div className="mb-6">
                  <Label className="text-[#3F3F3F] font-medium mb-2 block">
                    Message <span className="text-red-500">*</span>
                  </Label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    rows={5}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="message-input"
                  />
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit"
                  className="w-full bg-[#056839] text-white hover:bg-[#04522d] rounded-full h-12 text-base font-medium"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </form>

              {/* WEEE Notice */}
              <div className="mt-8 bg-[#D2E4D6] rounded-xl border border-[#69A488] p-6" data-testid="weee-notice">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#056839] text-white flex items-center justify-center flex-shrink-0">
                    <Info className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-[#3F3F3F]">
                      <strong className="text-[#056839]">WEEE Services Now Available:</strong> AfriCycle Zambia now offers EPR compliance support for electrical and electronic equipment (WEEE). Contact us to learn more about our e-waste compliance solutions.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
